<template>
  <page :load="doInit">
    <v-layout row wrap>
      <v-flex lg5 xs12 class="mavContainer">
        <v-card>
          <v-toolbar flat>
            <v-card-title>Total Users ({{totalUsers}})</v-card-title>
            <v-spacer></v-spacer>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn icon @click="doRefresh" v-on="on">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Refresh Users</span>
            </v-tooltip>
          </v-toolbar>
          <v-container fluid class="ma-0 mt-2">
            <v-flex xs12>
              <v-text-field placeholder="Search..." v-model="search" single-line full-width hide-details prepend-inner-icon="mdi-magnify" clearable dense/>
            </v-flex>
            <v-flex xs12>
              <v-divider></v-divider>
            </v-flex>
          </v-container>
          <v-list>
            <template v-if="totalUsers > 0">
              <v-list-item v-for="(user, index) in filteredUsers" :key="user.userId" @click="doSelectUser(index)">
                <v-list-item-avatar class="v-list-item__avatar">
                  <v-badge :color="getStatusColor(user.status, false)" :icon="getStatusIcon(user.status)" overlap bottom right>
                  <v-avatar :size="40" :color="getAvatarColor(user.userId)">
                    <span class="white--text">
                      {{formatInitials(user.firstName, user.lastName)}}
                    </span>
                  </v-avatar>
                  </v-badge>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{user.lastName}}, {{user.firstName}}</v-list-item-title>
                  <v-list-item-subtitle :class="getStatusColor(user.status, true)">{{ getTimeAgo(user.status, user.sessions[0].lastAccessTime) }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-else>
              <v-card-text>
                There is no user online
              </v-card-text>
            </template>
          </v-list>
        </v-card>
      </v-flex>
      <v-flex lg7 xs12 class="mavContainer">
        <template v-if="selectedIndex !== undefined">
          <v-card>
            <card-avatar :email="selectedUser.email" :userId="selectedUser.userId" :fname="selectedUser.firstName" :lname="selectedUser.lastName"/>
            <v-card-text>
              <v-container fluid grid-list-lg>
                <v-layout row wrap>
                  <v-card-title>
                    <div>
                      <v-icon>mdi-lan-connect</v-icon> Sessions
                    </div>
                  </v-card-title>
                  <v-spacer/>
                  <v-btn text color="red" @click="promptDC = true" :loading="submitting">
                    <div>
                      <v-icon>mdi-lan-disconnect</v-icon> Disconnect
                    </div>
                  </v-btn>
                  <v-flex xs12 v-for="(session, index) in selectedUser.sessions" :key="index">
                    <v-flex xs6>
                      <v-subheader class="queryLabel">IP Address</v-subheader>
                      <span class="queryValue">{{ session.ipAddress }}</span>
                    </v-flex>
                    <v-flex xs6>
                      <v-subheader class="queryLabel">Last Access Time</v-subheader>
                      <span class="queryValue">{{ formatDateTime(session.lastAccessTime) }}</span>
                    </v-flex>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
          </v-card>
        </template>
      </v-flex>
    </v-layout>
    <confirm v-model="promptDC" @ok="doDC">
      Do you wish to disconnect selected user from the system?
    </confirm>
  </page>
</template>

<script>
import CardAvatar from '@/components/reusable/CardAvatar'
import { initials } from '@/utils/name'
import { formatDisplayDateTime } from '@/utils/date'
import { extractMenu } from '@/utils/extract'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { getAvatarColor } from '@/utils/global'

export default {
  data() {
    return {
      users: [],
      search: '',
      selectedIndex: undefined,
      promptDC: false,
      menu: [],
      submitting: false
    }
  },
  computed: {
    totalUsers() {
      if (this.filteredUsers) {
        return this.filteredUsers.length
      } else {
        return 0
      }
    },
    filteredUsers() {
      if (this.search && this.search !== '') {
        const result = this.backendUsers.filter(user => {
          const fullName = user.firstName + ', ' + user.lastName
          return fullName.toLowerCase().indexOf(this.search.toLowerCase()) !== -1
        })
        return result
      } else {
        return this.backendUsers
      }
    },
    backendUsers() {
      if (this.users && this.users.length > 0) {
        return this.users.filter(x => x.mavModule === 'B')
      } else {
        return []
      }
    },
    selectedUser() {
      if (this.selectedIndex !== undefined) {
        return this.users[this.selectedIndex]
      } else {
        return {}
      }
    }
  },
  methods: {
    async doInit() {
      const apiURI = this.$store.state.adminUser.menu.sessions
      try {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        if (data) {
          this.users = data
        }
        this.menu = extractMenu(result.data._links)
      } catch (err) {
        throw err
      }
    },
    getTimeAgo(status, dateString) {
      const now = new Date()
      const past = new Date(dateString)

      const diffMs = now - past
      const diffSec = Math.floor(diffMs / 1000)

      switch (status) {
        case 'AWAY': {
          if (diffSec < 60) return `${diffSec} sec ago`

          const diffMin = Math.floor(diffSec / 60)
          if (diffMin < 60) return `${diffMin} min ago`

          const diffHr = Math.floor(diffMin / 60)
          if (diffHr < 24) return `${diffHr} hr ago`

          const diffDay = Math.floor(diffHr / 24)
          return `${diffDay} day ago`
        }
        case 'ONLINE':
          return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        default:
          return 'Not Available'
      }
    },
    getAvatarColor(userId) {
      return getAvatarColor(userId)
    },
    getStatusColor(status, isSubtitle) {
      switch (status) {
        case 'ONLINE':
          return 'green' + (isSubtitle === true ? '--text' : '')
        case 'AWAY':
          return 'orange' + (isSubtitle ? '--text' : '')
        default:
          return 'grey' + (isSubtitle ? '--text' : '')
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case 'ONLINE':
          return 'mdi-check'
        case 'AWAY':
          return 'mdi-clock'
        default:
          return 'mdi-minus'
      }
    },
    doRefresh() {
      this.doInit()
      this.search = ''
      this.selectedIndex = undefined
      this.promptDC = false
      this.submitting = false
    },
    formatInitials(fname, lname) {
      return initials(fname, lname)
    },
    formatDateTime(dateTime) {
      return formatDisplayDateTime(dateTime)
    },
    doSelectUser(userId) {
      this.selectedIndex = userId
    },
    async doDC() {
      const apiURI = this.menu.disconnect
      if (apiURI) {
        this.submitting = true
        try {
          const expand = {
            id: this.selectedUser.userId
          }
          const result = await this.$http.post(expandURI(apiURI, expand))
          const { data } = result
          if (data) {
            this.notify(data)
          }
          this.doRefresh()
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          this.submitting = false
        }
      }
    }
  },
  mixins: [
    MessageMixin
  ],
  components: {
    CardAvatar
  }
}
</script>

<style lang="scss">
.v-list-item__avatar {
  overflow: visible !important;
}
</style>
